<template>

  <div :id="page_slug" v-loading.body="loading.page">

    <div class="crumbs-bar">
      <el-row>
        <el-col :span="12">
          <el-breadcrumb separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ name: 'DashboardPage' }">Dashboard</el-breadcrumb-item>
            <el-breadcrumb-item>Products</el-breadcrumb-item>
          </el-breadcrumb>        
        </el-col>      
      </el-row>
    </div>

    <div class="actions-bar">
      <el-row>
        <el-col :span="8">
          <el-input placeholder="Search by name or SKU" v-on:keyup.native="whichSearchFunctionToUse" v-model="search_terms">
            <template slot="append"><el-button slot="append" icon="el-icon-search"></el-button></template>
          </el-input>
        </el-col>

        <el-col :span="14" :offset="2" class="text-right">
          <el-dropdown @command="handleCommand" trigger="click" :hide-on-click="true" :hide-timeout="1">
            <el-tooltip class="item" effect="light" content="Select to sort data" placement="top">
              <el-button>
                Sort By <i class="el-icon-caret-bottom"></i>
              </el-button>
            </el-tooltip>

            <el-dropdown-menu slot="dropdown" class="my_test">
              <el-dropdown-item v-for="(column, index) in sort_by_array" :key="index" :command="column"> 
                {{ column | prettyIndexName }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-dropdown trigger="click" :hide-on-click="true" :hide-timeout="1">
            <el-tooltip class="item" effect="light" content="Show or hide columns" placement="top">
              <el-button>
                Columns <i class="el-icon-caret-bottom"></i>
              </el-button>
            </el-tooltip>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-for="(column, index) in columns" :key="index">
                <el-checkbox v-model="columns[index]">{{index | prettyIndexName}}</el-checkbox>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <router-link :to="{ name: 'ProductAdd' }">
            <el-button :disabled="!hasRole('products.can_add')" type="default" >Add Product</el-button>
          </router-link>

          <download-excel
            class="el-button el-button--default"
            :data="products"
            :fields="product_headings"
            name="Products.xls">
            Download Products
          </download-excel>
        </el-col>
      </el-row>
    </div>

    <el-row>
      <el-table
        v-loading="loading.data"
        :data="products"
        border
        resizable="true"
        style="width: 100%">

        <el-table-column
          v-if="columns.id"
          prop="id"
          label="ID"
          width="115%">
        </el-table-column>

        <el-table-column
          v-if="columns.name"
          prop="name"
          label="Name">
        </el-table-column>

        <el-table-column
          v-if="columns.sku"
          prop="sku"
          label="SKU">
        </el-table-column>

        <el-table-column
          v-if="columns.stock_level"
          prop="stock_level"
          label="Stock Level">
        </el-table-column>

        <el-table-column
          v-if="columns.total_sold"
          prop="total_sold"
          label="Total Sold">
        </el-table-column>

        <el-table-column
          v-if="columns.specification"
          prop="specification"
          label="Specification">
        </el-table-column>

        <el-table-column
          v-if="columns.short_description"
          prop="short_description"
          label="Short Description">
        </el-table-column>

        <el-table-column
          v-if="columns.description"
          prop="description"
          label="Description">
        </el-table-column>

        <el-table-column
          v-if="columns.print_type_name"
          prop="print_type.name"
          label="Print Type">
        </el-table-column>

        <el-table-column
          v-if="columns.tax_class_name"
          prop="tax_class.name"
          label="Tax Class">
        </el-table-column>

        <el-table-column
          v-if="columns.created_user_id"
          prop="created_user_id"
          label="Created User ID">
        </el-table-column>

        <el-table-column
          v-if="columns.updated_user_id"
          prop="updated_user_id"
          label="Updated User ID">
        </el-table-column>

        <el-table-column
          v-if="columns.created_at"
          align="center"
          width="115%"
          prop="created_at"
          label="Created Date">
          <template slot-scope="scope">
            {{scope.row.created_at | moment(config.date_format_compact)}}
          </template>          
        </el-table-column>

        <el-table-column
          v-if="columns.updated_at"
          align="center"
          width="115%"
          prop="updated_at"
          label="Updated Date">
          <template slot-scope="scope">
            {{scope.row.updated_at | moment(config.date_format_compact)}}
          </template>          
        </el-table-column>

        <el-table-column
          v-if="columns.track_inventory"
          prop="track_inventory"
          label="Track Inventory"
          align="center"
          width="130%">
          <template slot-scope="scope">
            <el-switch
              disabled
              :active-value="1"
              :inactive-value="0"
              v-model="scope.row.track_inventory">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column
          v-if="columns.allow_backorder"
          prop="allow_backorder"
          label="Backorder"
          align="center"
          width="115%">
          <template slot-scope="scope">
            <el-switch
              disabled
              :active-value="1"
              :inactive-value="0"
              v-model="scope.row.allow_backorder">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column
          v-if="columns.is_editable"
          prop="is_editable"
          label="Editable"
          align="center"
          width="115%">
          <template slot-scope="scope">
            <el-switch
              disabled
              :active-value="1"
              :inactive-value="0"
              v-model="scope.row.is_editable">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column
          v-if="columns.enabled"
          prop="enabled"
          label="Enabled"
          align="center"
          width="115%">
          <template slot-scope="scope">
            <el-switch
              disabled
              :active-value="1"
              :inactive-value="0"
              v-model="scope.row.enabled">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column
          label="Actions"
          align="center"
          width="115%">
          <template slot-scope="scope">
            <router-link :to="{ name: 'ProductEdit', params: { id: scope.row.id }}">
              <el-button 
              :disabled="!hasRole('products.can_edit')"
              icon="el-icon-edit"
              type="default" 
              size="small"></el-button>
            </router-link>

            <el-button 
            :disabled="!hasRole('products.can_delete')"
            icon="el-icon-delete" 
            type="default" 
            size="small"
            @click="deleteProduct(scope.row.id)"></el-button>
          </template>
        </el-table-column>

      </el-table>
    </el-row>

    <el-row :gutter="0">
      <el-col :span="24" :xs="24" :sm="24" class="pagination">
        <el-pagination
          background
          @current-change="handleCurrentChange"
          :current-page.sync="pagination.current_page"
          :page-count="pagination.total_pages"
          :page-size="pagination.per_page"
          layout="total, prev, pager, next"
          :total="pagination.total">
        </el-pagination>
      </el-col>
    </el-row>

  <pre>{{$data}}</pre>

  </div>
</template>

<style>
.my_test {
  height: 250px !important;
  max-height: 250px !important;
  overflow-y: scroll !important;
}
</style>

<script>
  import Config from '@/store/Config.js';
  import Tables from '@/store/Tables.js';
  import store from '@/store/Store.js';

  export default {
    data: function() {
      return {
        page_alias: 'Product List',
        page_slug: 'product-list',

        loading: {
          page: false,
          data: false,
        },

        config: Config.config,
        permissions: store.permissions,

        columns: Tables.products.columns,
        order_by: Tables.products.order_by,
        sort_by: Tables.products.sort_by,
        sort_by_array: [],
        sort_by_field_name: 'id',
        sort_by_order: 'asc',

        search_terms: '',

        products: [],
        product_headings: {
          'ID': 'id',
          'Name': 'name',
          'URL Name': 'url_name',
          'SKU': 'sku',
          'Description': 'description',
          'Short-Description': 'short_description',
          'Enabled': 'enabled',
          'Track Inventory': 'track_inventory',
          'Stock Level': 'stock_level',
          'Total Sold': 'total_sold',
          'Retail Price': 'retail_price',
          'Product Weight': 'product_weight',
          'Specification': 'specification',
          'Size': 'size',
          'Is Shell': 'is_shell',
          'Is Tiered': 'is_tiered',
          'Product Type': 'product_types.name',
          'Tax Class': 'tax_class.name',
          'Created At': 'created_at',
          'Updated At': 'updated_at',
        },

        pagination: {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          next_page_url: '',
          prev_page_url: '',
          from: 0,
          to: 0,
        },
      };
    },

    mounted: function() {
      const me = this;
      me.getProducts();
      me.getSortByFieldFromTablesJS();
    },

    methods: {
      hasRole: function(role_name) {
        const me = this;

        if(me.permissions.user_roles.length > 0) {
          /* returns undefined if doesn't find the role */
          var role = _.find(me.permissions.user_roles, function(o) { 
            return o.name == role_name; 
          });

          /* doesn't enter if when undefined */
          if(role) {
            return true
          }
        }
        
        return false;
      },

      handleCommand(command) {
        const me = this;

        me.sort_by_field_name = command;
        me.whichSearchFunctionToUse();
      },

      getSortByFieldFromTablesJS: function() {
        const me = this;

        me.sort_by_array = Object.keys(me.sort_by);

        Object.keys(me.sort_by).forEach(function eachKey(key) { 
          if(me.sort_by[key] == true) {
            me.sort_by_field_name = key;
          }
        });
      },

      whichSearchFunctionToUse: function() {
        const me = this;

        if(me.search_terms == '') {
          me.getProducts();
        } else me.search();
      },

      search: function(goToPage) {
        console.log('search products function');
        const me = this;
        me.loading.data = true;

        me.$http.get(me.config.api + 'product-search', {params: {admin: 1, search_terms: me.search_terms, page: goToPage, sort_by: me.sort_by_field_name, order: me.sort_by_order}}).then(
          response => {
          console.log('Success Getting Data')  
          console.log(response.status);
          console.log(response.statusText);
          
          me.products = response.body.data;
         
          me.pagination.total = response.body.total;
          me.pagination.per_page = response.body.per_page;
          me.pagination.current_page = response.body.current_page;
          me.pagination.last_page = response.body.last_page;
          me.pagination.next_page_url = response.body.next_page_url;
          me.pagination.prev_page_url = response.body.prev_page_url;
          me.pagination.from = response.body.from;
          me.pagination.to = response.body.to;

          me.loading.data = false;
        }, response => {
          console.log('Error Getting Data');
          console.log(response.status);
          console.log(response.statusText);
          me.$notify({
              title: 'Error',
              message: response.statusText,
              type: 'error'
          });

          me.loading.data = false;
        });
      },

      getProducts: function(goToPage) {
        console.log('Get Products Function');
        const me = this;
        me.loading.data = true;

        me.$http.get(me.config.api + 'product', {params: {admin: 1, page: goToPage, sort_by: me.sort_by_field_name, order: me.sort_by_order}}).then(
          response => {
          console.log('Get Products Function - Success Getting Data')  

          me.products = response.body.data;

          me.pagination.total = response.body.total;
          me.pagination.per_page = response.body.per_page;
          me.pagination.current_page = response.body.current_page;
          me.pagination.last_page = response.body.last_page;
          me.pagination.next_page_url = response.body.next_page_url;
          me.pagination.prev_page_url = response.body.prev_page_url;
          me.pagination.from = response.body.from;
          me.pagination.to = response.body.to;

          me.loading.data = false;
        }, response => {
          console.log('Get Products Function - Error Getting Data');

          me.$notify({
            title: 'Error',
            message: response.statusText,
            type: 'error'
          });

          me.loading.data = false;
        });
      },

      handleCurrentChange(val) {
        const me = this;

        if(me.search_terms == '') {
          me.getProducts(val);
        } else {
          me.search(val);
        }

        window.scrollTo(0, 0);
      },

      deleteProduct: function(id) { 
        console.log('Delete Product Function');
        const me = this;

        me.$confirm('This will permanently delete the Product - Continue?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          me.$http.delete(me.config.api + 'product/{id}', {params: {id : id}}).then(
            response => {
            console.log('Delete Product Function - Success Deleting Data')  

            me.getProducts();

            me.$notify({
              title: 'Success',
              message: 'The Product Was Deleted',
              type: 'success',
            });
          }, response => {
            console.log('Delete Product Function - Error Getting Data');
          });  
        }).catch(() => {
          this.$message({
            type: 'info',
            message: 'Delete Canceled'
          });          
        });
      }
    },

    filters: {
      prettyIndexName(text) {
        var prettyName = text.replace(/_/g, ' ').toUpperCase();
        return prettyName
      }
    }
  }
</script>


// WEBPACK FOOTER //
// src/pages/products/ProductList.vue